import React from 'react';
import { Box, Typography } from '@mui/material';
import { COLOR } from './tokens';

export const STATUS_LABELS = {
    menu: 'Ready',
    playing: 'Playing',
    paused: 'Paused',
    over: 'Game over',
    ended: 'Ended',
};

/** Small status pill for a play phase (menu | playing | paused | over | ended). */
export function StatusChip({ phase }) {
    const label = STATUS_LABELS[phase];
    if (!label) return null;
    const live = phase === 'playing';
    return (
        <Box
            component="span"
            data-testid="game-status"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.25,
                borderRadius: 999,
                border: `1px solid ${live ? COLOR.primary : COLOR.borderStrong}`,
                color: live ? COLOR.primary : COLOR.textMuted,
                fontSize: '0.75rem',
                fontWeight: 600,
            }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: live ? COLOR.primary : COLOR.textSubtle }}
            />
            {label}
        </Box>
    );
}

/**
 * Page header shared by every game: title (+ accent word), subtitle, and an
 * optional row with the mode picker, status and live score.
 */
export default function GameHeader({
    title,
    titleAccent,
    accentColor = COLOR.primary,
    subtitle,
    mode = null,
    phase,
    score,
    scoreLabel = 'Score',
    compact = false,
}) {
    const hasMeta = mode || phase || (score != null && score !== '');
    return (
        <Box component="header" sx={{ textAlign: 'center', mb: compact ? 2 : 4 }} data-testid="game-header">
            <Typography
                variant="h3"
                component="h1"
                sx={{
                    color: COLOR.text,
                    fontWeight: 800,
                    mb: 1,
                    letterSpacing: '-1px',
                    fontSize: { xs: '2rem', md: '2.5rem' },
                }}
            >
                {title}{' '}
                {titleAccent ? <span style={{ color: accentColor }}>{titleAccent}</span> : null}
            </Typography>
            {subtitle ? (
                <Typography variant="body1" sx={{ color: COLOR.textMuted, maxWidth: 720, mx: 'auto' }}>
                    {subtitle}
                </Typography>
            ) : null}
            {hasMeta ? (
                <Box
                    sx={{
                        mt: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    {mode}
                    {phase ? <StatusChip phase={phase} /> : null}
                    {score != null && score !== '' ? (
                        <Typography
                            component="span"
                            className="evx-tabular"
                            data-testid="game-header-score"
                            sx={{ color: COLOR.text, fontWeight: 700 }}
                        >
                            {scoreLabel} {score}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}
